/**
 * #2069 — one app-made offer per idea found under a chat reply.
 * Ideas are proposed story_entities from the live pass and the summary sweep.
 * Person, place, thing, and event only. No per-reply cap.
 */

import {
  CAST_PEOPLE,
  CAST_PLACE,
  IDEA_KIND_EVENT,
  IDEA_KIND_THING,
} from './writerCopy';

export const CARD_OFFER_TYPES = new Set(['character', 'place', 'object', 'event']);
export const CARD_OFFER_ORIGINS = new Set(['live', 'summary_sweep']);

export function isCardOffer(entity) {
  if (!entity) return false;
  const type = String(entity.entity_type || '').toLowerCase();
  if (!CARD_OFFER_TYPES.has(type)) return false;
  if (!CARD_OFFER_ORIGINS.has(entity.originating_pass)) return false;
  if (entity.offer_saved) return true;
  return entity.status === 'proposed';
}

/** Character and place have their own Notes categories. World and Plot hold one note each, so a thing or an event stays Unsorted. */
export function noteCategoryForOffer(entityType) {
  const type = String(entityType || '').toLowerCase();
  if (type === 'character') return 'Character';
  if (type === 'place') return 'Setting/Location';
  return 'Unsorted';
}

export function offerKindLabel(entityType) {
  const type = String(entityType || '').toLowerCase();
  if (type === 'character') return CAST_PEOPLE;
  if (type === 'place') return CAST_PLACE;
  if (type === 'object') return IDEA_KIND_THING;
  if (type === 'event') return IDEA_KIND_EVENT;
  return '';
}

/** Types the menu offers, in menu order. */
export const OFFER_MENU_TYPES = ['character', 'place', 'object', 'event'];

/** Drop every offer with this name, ignoring case and outer spaces. */
export function dropOffersNamed(offers, name) {
  const key = String(name || '').trim().toLowerCase();
  if (!key) return offers || [];
  return (offers || []).filter((row) => String(row && row.name || '').trim().toLowerCase() !== key);
}

function speakerOf(message) {
  return message?.speaker || message?.role || '';
}

/**
 * Attach each offer to the reply that follows the message it was found in.
 * A writer message with no reply after it (Mute) keeps the offer on that message.
 * Offers whose message is not in this thread come back as loose.
 */
export function groupCardOffers(messages, entities) {
  const bySource = new Map();
  const loose = [];
  (entities || []).filter(isCardOffer).forEach((offer) => {
    const sourceId = offer.source_message_id ? String(offer.source_message_id) : '';
    if (!sourceId) {
      loose.push(offer);
      return;
    }
    const list = bySource.get(sourceId) || [];
    list.push(offer);
    bySource.set(sourceId, list);
  });

  const byMessage = new Map();
  const claimed = new Set();
  const thread = messages || [];
  thread.forEach((message, index) => {
    const id = message?.id != null ? String(message.id) : '';
    const found = id ? bySource.get(id) : null;
    if (!found) return;
    claimed.add(id);
    let targetId = id;
    if (speakerOf(message) === 'user') {
      for (let next = index + 1; next < thread.length; next += 1) {
        const later = thread[next];
        if (speakerOf(later) === 'assistant') {
          targetId = String(later.id);
          break;
        }
        if (speakerOf(later) === 'user') break;
      }
    }
    const existing = byMessage.get(targetId) || [];
    byMessage.set(targetId, existing.concat(found));
  });

  bySource.forEach((found, sourceId) => {
    if (!claimed.has(sourceId)) loose.push(...found);
  });
  return { byMessage, loose };
}

/** Drop the first row with this id. A shared id must not clear the other cards. */
export function dropOneOffer(offers, id) {
  const key = String(id || '');
  if (!key) return offers || [];
  const list = offers || [];
  const index = list.findIndex((row) => String(row && row.id || '') === key);
  if (index < 0) return list;
  return list.slice(0, index).concat(list.slice(index + 1));
}

/**
 * A reload paints the rows the server still returns, minus ids this visit
 * already dismissed. A non-list response is not a reason to clear the cards.
 */
export function visibleCardOffers(rows, hiddenIds) {
  if (!Array.isArray(rows)) return null;
  const hidden = hiddenIds || new Set();
  return rows.filter((row) => !hidden.has(String(row && row.id || '')));
}
